import { HttpClient } from '@angular/common/http';
import { APP_INITIALIZER, DEFAULT_CURRENCY_CODE, InjectionToken, LOCALE_ID, NgModule } from '@angular/core';
import { NgRxStoreModule } from '@store/store.module';
import { TranslateCompiler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { PlatformLocation, registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import localePt from '@angular/common/locales/pt';
import localeEn from '@angular/common/locales/en';
import { MatDialogModule } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { UTCDateAdapter } from '@core/date/utc-date-adapter.service';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { es as esDateFnsLocale, es } from 'date-fns/locale';
import { defaultLanguageFn } from '@core/services/translation/default-lang';
import { Store } from '@ngrx/store';
import { StoreState } from '@store/store.state';
import { appActions } from '@store/actions/app.actions';
import { environment } from '@env/environment';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { setDefaultOptions } from 'date-fns';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';

registerLocaleData(localeEs, 'es');
registerLocaleData(localePt, 'pt');
registerLocaleData(localeEn, 'en');

setDefaultOptions({ locale: es, weekStartsOn: 1 });

export const BROADCAST_CHANNEL = new InjectionToken<BroadcastChannel>('BROADCAST_CHANNEL');

@NgModule({
  imports: [
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient, PlatformLocation],
      },
      isolate: false,
      defaultLanguage: defaultLanguageFn(),
      compiler: {
        provide: TranslateCompiler,
        useClass: TranslateMessageFormatCompiler,
      },
    }),
    NgRxStoreModule,
    MatDialogModule,
    MatSnackBarModule,
    NgIdleKeepaliveModule.forRoot(),
  ],
  providers: [
    { provide: LOCALE_ID, useValue: 'es' },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        color: 'accent',
        appearance: 'fill',
        floatLabel: 'auto',
        hideRequiredMarker: true,
        subscriptSizing: 'dynamic',
      },
    },
    {
      provide: MAT_RIPPLE_GLOBAL_OPTIONS,
      useValue: {
        disabled: true,
      },
    },
    { provide: MAT_DATE_LOCALE, useValue: esDateFnsLocale },
    { provide: DateAdapter, useClass: UTCDateAdapter, deps: [MAT_DATE_LOCALE] },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: { touchLongPressShowDelay: 500, touchendHideDelay: 3000, hideDelay: 3000, position: 'above' },
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'dd/MM/yyyy',
        },
        display: {
          dateInput: 'dd/MM/yyyy',
          monthLabel: 'LLLL',
          monthYearLabel: 'MMM yyyy',
          dateA11yLabel: 'PPPP',
          monthYearA11yLabel: 'MMMM yyyy',
        },
      },
    },
    { provide: BROADCAST_CHANNEL, useValue: new BroadcastChannel(`acciona-energia-clientes-${environment.env}`) },
    {
      provide: APP_INITIALIZER,
      useFactory: (store: Store<StoreState>) => () => store.dispatch(appActions.hydrateApp()),
      multi: true,
      deps: [Store<StoreState>],
    },
  ],
})
export class CoreModule {}

// Required for AOT compilation
function httpLoaderFactory(http: HttpClient, platformLocation: PlatformLocation): TranslateHttpLoader {
  return new TranslateHttpLoader(http, platformLocation.getBaseHrefFromDOM() + 'assets/i18n/');
}
